import type { Comment } from '@eigenpal/docx-editor-core/types/content';
import type { WorkspaceQuestion } from '../types';

/**
 * Comment↔question order correlation, shared by the docx pane's selection-sync
 * (active question → comment to scroll to) and its position extraction (every
 * comment → its question, to place the aligned Q&A rows).
 *
 * The review-render injects exactly one native comment per proposed answer, in
 * question order. So the Nth question-with-an-answer maps to the Nth top-level
 * comment (replies — which carry a `parentId` — are excluded, and comments are
 * sorted by id to recover insertion order).
 *
 * NOTE: This is an order-correlation heuristic pending a precise anchor↔editor
 * mapping (the editor does not yet expose the source anchor of a comment). It
 * holds only while the answer set and the comment set stay 1:1 and in the same
 * order — needs real-browser validation against a multi-answer document.
 */

/**
 * The proposed-answer set, in question order — the exact set (and order) the
 * review-render commented on. The Nth entry here is what the Nth top-level
 * comment refers to.
 */
export function answeredQuestionsInOrder(questions: WorkspaceQuestion[]): WorkspaceQuestion[] {
  return questions
    .filter((q) => (q.suggested_response ?? '').trim().length > 0)
    .sort((a, b) => a.index - b.index);
}

/**
 * Top-level comments (replies excluded) sorted by id, recovering the insertion
 * order in which the render injected them.
 */
export function topLevelCommentsInOrder(comments: Comment[]): Comment[] {
  return comments.filter((c) => c.parentId == null).sort((a, b) => a.id - b.id);
}

/**
 * Correlate the active question to a loaded editor comment id by ORDER.
 *
 * Returns null when correlation can't resolve (question has no answer, isn't in
 * the proposed set, or there are fewer comments than its ordinal) so the caller
 * can no-op gracefully.
 */
export function correlateCommentId(
  activeQuestionId: string | null,
  questions: WorkspaceQuestion[],
  comments: Comment[],
): number | null {
  if (!activeQuestionId) return null;

  const answered = answeredQuestionsInOrder(questions);
  const ordinal = answered.findIndex((q) => q.question_id === activeQuestionId);
  if (ordinal < 0) return null;

  const topLevel = topLevelCommentsInOrder(comments);
  const match = topLevel[ordinal];
  return match ? match.id : null;
}

/**
 * Build the full commentId → questionId map by the SAME order correlation used
 * for selection-sync: the Nth top-level comment (by id) maps to the Nth
 * answered question (by index). Only the overlapping prefix is mapped — extra
 * comments or extra answered questions on either side are left out.
 *
 * Position extraction uses this to label each measured comment marker with the
 * question whose aligned row should sit at that vertical position.
 */
export function buildCommentToQuestionMap(
  questions: WorkspaceQuestion[],
  comments: Comment[],
): Map<number, string> {
  const answered = answeredQuestionsInOrder(questions);
  const topLevel = topLevelCommentsInOrder(comments);
  const map = new Map<number, string>();
  const pairs = Math.min(answered.length, topLevel.length);
  for (let i = 0; i < pairs; i += 1) {
    map.set(topLevel[i].id, answered[i].question_id);
  }
  return map;
}
